// PAY1 Slice 3 (docs/build-briefs/PAY1-S3-standup-onboarding.md, sections 2 and 3.6). The words and shapes the
// software owner console and the franchisee's Settings share for a location's card payments: the DTOs the server sends,
// the state pill, the account line and the relative times.
//
// COPY RULE (owner, 2026-10-09): labels and states only. No explanatory sentence lives here; the brief lists every
// mockup sentence that was dropped. Pure functions, no DOM, so they are tested directly.

export type CardPaymentsKind =
  | "no_legal_entity"
  | "not_set_up"
  | "creating"
  | "not_started"
  | "in_progress"
  | "restricted"
  | "enabled"
  | "disconnected";

export interface RequirementItemDto {
  group: string;
  label: string;
  pending: boolean;
}

/** What Settings is sent for a location (no mapping id, no Stripe code, no confirmation). */
export interface LocationCardPaymentsDto {
  locationId: string;
  locationName: string;
  kind: CardPaymentsKind;
  masked: string | null;
  livemode: boolean | null;
  creatingMinutes: number | null;
  requirements: RequirementItemDto[];
  reasonLabel: string | null;
  lastCheckedAt: string | null;
  /** STRIPETAB1: when the owner last came back from Stripe (the server's last return read), or null. */
  returnedAt?: string | null;
}

export interface CardPaymentsForLocationDto {
  thisLocation: LocationCardPaymentsDto;
  otherLocations: LocationCardPaymentsDto[];
  mode: "test" | "live";
}

export interface ConfirmationDto {
  confirmedOn: string;
  confirmedBy: string;
  reference: string;
  recordedByName: string | null;
  recordedAt: string;
}

/** What the console is sent for a location. */
export interface ConsoleLocationCardPaymentsDto extends LocationCardPaymentsDto {
  mappingId: string | null;
  stripeCode: string | null;
  confirmation: ConfirmationDto | null;
  legalName: string | null;
  ownerEmail: string | null;
  canAssignLegal?: boolean;
}

export interface ConsoleCardPaymentsDto {
  mode: "test" | "live";
  /** PAY1-S3-F2 (DEC-353): the signed-in software owner holds platform.merchant_account.manage. False shows the card
   * read-only: no set-up or confirmation button is rendered. */
  canManage: boolean;
  locations: ConsoleLocationCardPaymentsDto[];
}

export type Tone = "good" | "warn" | "bad" | "neutral";

export interface Pill {
  label: string;
  tone: Tone;
}

/** A set-up open at least this many minutes with no account stored has stopped, not just started. */
export const SETUP_STOPPED_AFTER_MINUTES = 2;

export function pillOf(kind: CardPaymentsKind, creatingMinutes: number | null = null): Pill {
  switch (kind) {
    case "no_legal_entity":
      return { label: "No legal business", tone: "neutral" };
    case "not_set_up":
      return { label: "Not set up", tone: "neutral" };
    case "creating":
      return (creatingMinutes ?? 0) >= SETUP_STOPPED_AFTER_MINUTES
        ? { label: "Set-up stopped", tone: "bad" }
        : { label: "Creating", tone: "warn" };
    case "not_started":
    case "in_progress":
      return { label: "Setting up", tone: "warn" };
    case "restricted":
      return { label: "Restricted", tone: "bad" };
    case "enabled":
      return { label: "Card payments on", tone: "good" };
    case "disconnected":
      return { label: "Disconnected", tone: "bad" };
  }
}

/** STRIPETAB1: Stripe takes a few minutes after the owner returns. For this long after the last return, a location that
 * is not on yet reads as "reviewing", never as something the owner still has to do. */
export const REVIEW_WINDOW_MINUTES = 15;
/** How often a page showing the reviewing state asks the server again while it is visible. */
export const REVIEW_POLL_MS = 30_000;
export const REVIEWING_LINE =
  "Stripe is reviewing your details. This can take a few minutes. This page updates on its own.";
export const ONE_MORE_THING = "Stripe asked for one more thing";

export type ReviewState = "reviewing" | "more" | "none";

/** "reviewing": inside the window after a return and not on yet. "more": the window is over and Stripe still lists
 * something (normal tone, with Continue). "none": anything else, drawn as before (a rejected or disconnected account is
 * a real refusal and stays red). */
export function reviewStateOf(here: LocationCardPaymentsDto, now: Date): ReviewState {
  const kind = here.kind;
  if (kind !== "not_started" && kind !== "in_progress" && kind !== "restricted") return "none";
  if (kind === "restricted" && here.reasonLabel === "Rejected") return "none";
  if (here.returnedAt === undefined || here.returnedAt === null) return "none";
  const minutes = Math.max(0, now.getTime() - Date.parse(here.returnedAt)) / 60_000;
  if (minutes < REVIEW_WINDOW_MINUTES) return "reviewing";
  return here.requirements.some((r) => r.pending) ? "more" : "none";
}

/** The pill a location wears: the reviewing and one-more-thing states are never the red Restricted. */
export function reviewPill(here: LocationCardPaymentsDto, now: Date): Pill {
  const state = reviewStateOf(here, now);
  if (state === "reviewing") return { label: "Reviewing", tone: "warn" };
  if (state === "more") return { label: "Setting up", tone: "warn" };
  return pillOf(here.kind, here.creatingMinutes);
}

/** `Owner returned from Stripe 4 min ago`, for the console, or null when there was no return. */
export function returnedText(returnedAt: string | null | undefined, now: Date): string | null {
  return returnedAt === undefined || returnedAt === null ? null : `Owner returned from Stripe ${agoText(returnedAt, now)}`;
}

/** `Stripe account acct_...4821`, or null with no account. */
export function accountLine(masked: string | null): string | null {
  return masked === null ? null : `Stripe account ${masked}`;
}

/** `10 min ago`, `3 hours ago`, `2 days ago`, `just now`. Never a negative or a fraction. */
export function agoText(iso: string, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  return minutesAgoText(minutes);
}

export function minutesAgoText(minutes: number): string {
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

export function checkedText(lastCheckedAt: string | null, now: Date): string | null {
  return lastCheckedAt === null ? null : `Checked ${agoText(lastCheckedAt, now)}`;
}

export function stoppedText(creatingMinutes: number): string {
  return `Stopped ${minutesAgoText(creatingMinutes)}`;
}

/** `Oct 8` from a `YYYY-MM-DD` date or an ISO instant. */
export function shortDate(value: string): string {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date =
    day === null ? new Date(value) : new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** The account line of a confirmation: facts only. */
export function confirmationText(confirmation: ConfirmationDto): string {
  const by =
    confirmation.recordedByName === null ? "" : ` Recorded by ${confirmation.recordedByName}.`;
  return `Confirmed ${shortDate(confirmation.confirmedOn)}. ${confirmation.confirmedBy} at Stripe. Ref ${confirmation.reference}.${by}`;
}

/** The marker Stripe's return and refresh URLs carry on the app root. */
export type CardPaymentsArrival = "return" | "refresh";

/** Reads `?card_payments=return|refresh` from a location search string. */
export function readArrival(search: string): CardPaymentsArrival | undefined {
  const value = new URLSearchParams(search).get("card_payments");
  return value === "return" || value === "refresh" ? value : undefined;
}

/** The same URL with the marker removed (everything else, including other query keys and the hash, kept). */
export function withoutArrival(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("card_payments");
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
}

/** The host the simulated provider mints its fake onboarding links on. Followed in test mode only. */
export const SIMULATED_ONBOARDING_HOST = "onboarding.simulated.invalid";

/**
 * PAY1-S3-F1 item 3. The page follows an onboarding link only when it is https on a Stripe host (connect.stripe.com
 * or a subdomain of stripe.com), with no embedded credentials, or, in test mode only, the simulated provider's own
 * host. Returns the URL to follow, or null to refuse. A lookalike (`connect.stripe.com.evil.test`, `evilstripe.com`),
 * a non-https scheme and an unparsable string are all refused.
 */
export function onboardingNavigationTarget(
  rawUrl: unknown,
  mode: "test" | "live" | undefined,
): string | null {
  if (typeof rawUrl !== "string" || rawUrl.length === 0 || rawUrl.length > 2048) return null;
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" || parsed.username !== "" || parsed.password !== "") return null;
  const host = parsed.hostname.toLowerCase();
  const onStripe = host === "stripe.com" || host.endsWith(".stripe.com");
  if (onStripe || (mode === "test" && host === SIMULATED_ONBOARDING_HOST)) {
    return parsed.href;
  }
  return null;
}

/** A franchisee never sees what is still the software owner's to do: a set-up under way (`creating`) and a location with
 * no legal business both read as not set up. */
export function tenantKind(view: LocationCardPaymentsDto): LocationCardPaymentsDto["kind"] {
  return view.kind === "creating" || view.kind === "no_legal_entity" ? "not_set_up" : view.kind;
}

/** Whether the owner has something to finish on Stripe's page. */
export function canContinue(view: LocationCardPaymentsDto): boolean {
  const kind = tenantKind(view);
  if (kind === "not_started" || kind === "in_progress") return true;
  return kind === "restricted" && view.requirements.some((r) => r.pending);
}


/** CARDNUDGE1: what the Open Tabs card says while this location's card payments are not on. Pure: the page draws it. */
export interface CardNudgeView {
  title: string;
  /** What Stripe still needs, as the labels the Settings section shows. Empty when there is nothing to list. */
  needs: string[];
  /** One plain line, or null. */
  line: string | null;
  /** Whether "Continue on Stripe" is offered. */
  button: boolean;
  state: "action" | "checking" | "setup" | "disconnected";
}

export const CARD_NUDGE_TITLE = "Finish turning on card payments";
export const CARD_NUDGE_BUTTON = "Continue on Stripe";
export const CARD_NUDGE_CHECKING = "Stripe is checking your details";
export const CARD_NUDGE_SETTING_UP = "Your card account is being set up.";
export const CARD_NUDGE_DISCONNECTED = "Card payments are disconnected.";

/** Null when the card is not shown: card payments are on, or the session may not onboard. `missingLine` is what the
 * location itself still has to fill in before an account can be made, when the screen knows. */
export function cardNudgeView(
  here: LocationCardPaymentsDto,
  canOnboard: boolean,
  missingLine: string | null,
  now: Date = new Date(),
): CardNudgeView | null {
  if (!canOnboard) return null;
  const kind = tenantKind(here);
  if (kind === "enabled") return null;
  if (kind === "not_set_up") {
    return {
      title: CARD_NUDGE_TITLE,
      needs: [],
      line: missingLine ?? CARD_NUDGE_SETTING_UP,
      button: false,
      state: "setup",
    };
  }
  if (kind === "disconnected") {
    return {
      title: CARD_NUDGE_TITLE,
      needs: [],
      line: CARD_NUDGE_DISCONNECTED,
      button: false,
      state: "disconnected",
    };
  }
  const review = reviewStateOf(here, now);
  if (review === "reviewing") {
    return { title: CARD_NUDGE_TITLE, needs: [], line: REVIEWING_LINE, button: false, state: "checking" };
  }
  const needs = here.requirements.filter((r) => r.pending).map((r) => r.label);
  if (review === "more") {
    return { title: ONE_MORE_THING, needs, line: null, button: true, state: "action" };
  }
  const checking = needs.length === 0 && here.requirements.length > 0;
  if (checking || !canContinue(here)) {
    return {
      title: CARD_NUDGE_TITLE,
      needs: [],
      line: CARD_NUDGE_CHECKING,
      button: false,
      state: "checking",
    };
  }
  return { title: CARD_NUDGE_TITLE, needs, line: null, button: true, state: "action" };
}
